import Image from 'next/image';
import { FunctionComponent, useCallback, useId, useRef, useState } from 'react';
import styles from './styles.module.scss';
import Button from '../../../components/Button';
import { GetServerSideProps } from 'next';
import {
    GetEducatorResult,
    getEducatorDetails,
} from '../../../pages_components/[educator_param]/getEducatorDetails';
import { CademyIcon } from '../../../components/Icons';
import { PublicEducator } from '@cademy-package/sdk-component-types';
import ClaimProfileFormPage from '../../../components/ClaimProfile/Form';
import Link from 'next/link';
import BaseLayout from '../../../layouts/BaseLayout';
import CustomRecordPageView from '../../../types/CustomRecordPageView';
import useLegacyPageViewAnalytics from '../../../hooks/useLegacyPageViewAnalytics';
import ClaimProfileByEmailPage from '../../../components/ClaimProfile/ByEmail';
import ClaimProfileInitial from '../../../components/ClaimProfile/Initial';
import { Request } from '@shared/request';
import { CademyError } from '@shared/domain-shared';
import Alert from '../../../components/Alert';
import { useCaptcha } from '../../../hooks/useCaptcha';
import { getIAm } from '../../../services/IAm/getIAm';

export type EducatorAboutPageProps = {} & Omit<GetEducatorResult, 'internalReviewSummary'>;

const ClaimedProfileLandingPage: FunctionComponent<{ educator: PublicEducator }> = ({
    educator,
}) => {
    return (
        <section className={styles.claim_profile_container}>
            <div className={styles.educator_info_row}>
                <div className={`rounded_image ${styles.educator_logo}`}>
                    <Image
                        src={
                            educator.logo_url
                                ? educator.logo_url
                                : `${process.env.NEXT_PUBLIC_ASSETS_URL}/images/educator_fallback_logo.svg`
                        }
                        alt={`${educator.name} logo`}
                        fill
                        priority={true}
                        sizes="110px"
                    />
                </div>
                <div className={styles.educator_info}>
                    <h3>{educator.name}</h3>
                    <a href={`${educator.links?.web}`} target="_blank" rel="noopener noreferrer">
                        {educator.links?.web}
                    </a>
                </div>
                <CademyIcon className={styles.cademy_icon} height={50} width={50} />
            </div>
            <h1>Profile Claimed!</h1>
            <p>This profile has already been claimed. Please return to the previous page.</p>
            <Link href={`/${educator.slug}`}>
                <Button preset="primary">Close</Button>
            </Link>
        </section>
    );
};

type State = 'INITIAL' | 'VERIFY BY EMAIL' | 'VERIFY BY FORM' | 'CLAIMED';

const manualVerificationDomainRegex = /^.*(\.gov|\.ac\.uk|\.edu)$/;

const canEducatorBeClaimedByEmail = (educator: PublicEducator) => {
    if (educator.manual_verification_required === true) {
        return false;
    }
    if (!educator.redacted_email) {
        return false;
    }
    if (manualVerificationDomainRegex.test(educator.redacted_email)) {
        return false;
    }
    if (educator.links?.web && manualVerificationDomainRegex.test(educator.links.web)) {
        return false;
    }
    return true;
};

const ClaimProfilePage: FunctionComponent<EducatorAboutPageProps> & CustomRecordPageView = ({
    educator,
}) => {
    const id = useId();
    const [state, setState] = useState<State>(educator.claimed ? 'CLAIMED' : 'INITIAL');
    const [error, setError] = useState<null | CademyError>(null);
    const [captchaElement, getCaptchaToken] = useCaptcha();

    useLegacyPageViewAnalytics(async (analytics, pathname, url) => {
        await analytics.record.educatorProfileView(educator._id, pathname, url);
    });

    const onStartClaimClick = useCallback(() => {
        if (canEducatorBeClaimedByEmail(educator)) {
            setState('VERIFY BY EMAIL');
        } else {
            setState('VERIFY BY FORM');
        }
    }, [educator, setState]);

    const onVerifyAnotherWayClick = useCallback(() => {
        setState('VERIFY BY FORM');
    }, [setState]);

    const onSendVerificationClick = useCallback(async () => {
        setError(null);
        const request = new Request();
        try {
            const token = await getCaptchaToken();
            await request.post(
                `${process.env.NEXT_PUBLIC_CADEMY_API_URL}/admins/generate-claim-code`,
                {
                    educatorId: educator._id,
                    captchaToken: token,
                }
            );
            window.location.href = `${process.env.NEXT_PUBLIC_ADMIN_URL}/claim-profile?educator_param=${encodeURIComponent(educator.slug)}`;
        } catch (error) {
            if (error instanceof CademyError) {
                setError(error);
            } else {
                console.error(error);
                setError(
                    new CademyError(
                        '/generate-claim-code',
                        'Unknown error',
                        'An unknown error occurred while generating you a claim code',
                        500
                    )
                );
            }
        }
    }, [educator, setError]);

    return (
        <BaseLayout
            title={`Claim ${educator.name} - Cademy`}
            description={`Claim the ${educator.name} educator profile.`}
            path={`${educator.slug}/claim-profile`}
        >
            <style jsx global>{`
                body {
                    background-color: var(--bg-3);
                }
            `}</style>
            <article
                aria-labelledby={`${id}-account-claim-entry`}
                className={styles.claim_profile_page}
            >
                {error ? <Alert preset="danger">{error.detail || error.title}</Alert> : null}
                {state === 'CLAIMED' ? <ClaimedProfileLandingPage educator={educator} /> : null}
                {state === 'INITIAL' ? (
                    <ClaimProfileInitial
                        educator={educator}
                        onStartClaimClick={onStartClaimClick}
                    />
                ) : null}
                {state === 'VERIFY BY EMAIL' ? (
                    <ClaimProfileByEmailPage
                        educator={educator}
                        onVerifyAnotherWayClick={onVerifyAnotherWayClick}
                        onSendVerificationClick={onSendVerificationClick}
                    />
                ) : null}
                {state === 'VERIFY BY FORM' ? <ClaimProfileFormPage educator={educator} /> : null}
            </article>
            {captchaElement}
        </BaseLayout>
    );
};

ClaimProfilePage.customRecordPageView = true;

export const getServerSideProps: GetServerSideProps<
    EducatorAboutPageProps,
    { educator_param: string }
> = async (context) => {
    if (!context.params?.educator_param) {
        throw new Error('Missing educator param');
    }
    const result = await getEducatorDetails(context.params.educator_param, { isMarketplace: true });
    if (!result) {
        return {
            notFound: true,
        };
    }
    return {
        props: {
            ...result,
            initialIAm: await getIAm(context.req.cookies),
        },
    };
};

export default ClaimProfilePage;
